// ----------------------------------------------------------------------------
// Blocks the admin app while the current church is soft-deleted: explains the
// recovery window and lets an admin restore it, sign out, or delete it for good
// (after typing its name). Same behaviour as the shared RestoreOrganizationGate
// the desktop apps use, drawn with the redesign's kit. The date maths and the
// "is it deleted" rule are in lib/churchDeletion.
// ----------------------------------------------------------------------------

import { useMemo, useState, type ReactNode } from 'react';
import { CalendarDays, TriangleAlert } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { RECOVERY_WINDOW_DAYS, getPurgeDate, isChurchSoftDeleted } from '@app/lib/churchDeletion';
import { churchesApi } from '@app/api/organization';
import { Banner, Button, Field, Input, Modal } from '@app/components/ui';
import { logger } from '@app/utils/logger';

const longDate = (d: Date) => d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

export function RestoreChurchGate({ children }: { children: ReactNode }) {
  const { church, isLoading, refetchChurch } = useChurch();
  const { logout } = useAuth();
  const [restoring, setRestoring] = useState(false);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const purgeDate = useMemo(() => getPurgeDate(church?.deletedAt ?? null), [church?.deletedAt]);

  // Wait for the church before deciding what to show.
  if (isLoading || !church || !isChurchSoftDeleted(church)) return <>{children}</>;

  const busy = restoring || purging;

  const restore = async () => {
    setError(null);
    setRestoring(true);
    try {
      await churchesApi.restore(church.id);
      await refetchChurch();
    } catch (err: any) {
      logger.error('Failed to restore organization', err);
      setError(err?.message || 'We couldn’t restore your church. Try again.');
    } finally {
      setRestoring(false);
    }
  };

  const signOut = async () => {
    try {
      await logout();
    } finally {
      window.location.href = '/login';
    }
  };

  const purge = async () => {
    if (typed !== church.name) return;
    setError(null);
    setPurging(true);
    try {
      await churchesApi.permanentlyDelete(church.id);
      try {
        await logout();
      } finally {
        window.location.href = '/login';
      }
    } catch (err: any) {
      logger.error('Failed to permanently delete organization', err);
      setError(err?.message || 'We couldn’t delete your church. Try again.');
      setPurging(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <Modal open onClose={() => undefined} dismissible={false} showClose={false} title={`${church.name} is scheduled for deletion`} width={500}>
        <div className="flex flex-col gap-4" data-testid="restore-organization-gate">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 rounded-[13px] bg-amb-bg text-amb flex items-center justify-center shrink-0" aria-hidden="true">
              <TriangleAlert size={20} />
            </span>
            <p className="text-[14px] text-t3 leading-[1.55]">
              An admin deleted this church, so it’s hidden from members. Its people, songs and services are kept during the recovery window. Restore it now,
              sign out and decide later, or delete it for good.
            </p>
          </div>

          {purgeDate && (
            <Banner tone="amb">
              <span className="flex items-center gap-2" data-testid="purge-date-banner">
                <CalendarDays size={16} className="shrink-0" aria-hidden="true" />
                <span>
                  Permanently deleted on <strong data-testid="purge-date">{longDate(purgeDate)}</strong> ({RECOVERY_WINDOW_DAYS}-day recovery window).
                </span>
              </span>
            </Banner>
          )}

          {error && (
            <Banner tone="acc" role="alert">
              {error}
            </Banner>
          )}

          {confirmOpen && (
            <div className="rounded-[16px] border p-3.5 flex flex-col gap-3 bg-acc-bg" style={{ borderColor: 'rgba(208,48,69,.35)' }} data-testid="purge-confirm-panel">
              <p className="text-[13.5px] font-semibold text-acc">This deletes the church and everything in it. It can’t be undone.</p>
              <Field
                label={
                  <>
                    Type <strong className="text-t1">{church.name}</strong> to confirm
                  </>
                }
                htmlFor="purge-confirm"
              >
                <Input id="purge-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={church.name} data-testid="purge-confirm-input" autoComplete="off" className="!bg-card" />
              </Field>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirmOpen(false);
                    setTyped('');
                  }}
                  disabled={purging}
                  className="!bg-card"
                >
                  Cancel
                </Button>
                <Button className="flex-1" onClick={purge} disabled={typed !== church.name || purging} loading={purging} data-testid="confirm-permanent-delete-button">
                  {purging ? 'Deleting…' : 'Delete forever'}
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-1">
            <Button shape="form" size="lg" block onClick={restore} disabled={busy} loading={restoring} data-testid="restore-organization-button">
              {restoring ? 'Restoring…' : 'Restore church'}
            </Button>
            <Button variant="ghost" shape="form" size="lg" block onClick={signOut} disabled={busy}>
              Sign out
            </Button>
            <Button variant="plain" size="md" className="!text-acc self-center" onClick={() => setConfirmOpen(true)} disabled={busy || confirmOpen} data-testid="delete-now-button">
              Delete forever now
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
